import React, { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  Plus, 
  Building2,
  Globe,
  Loader2,
  MapPin,
  Edit,
  Upload,
  Trash2,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const countries = [
  { code: 'UAE', name: 'United Arab Emirates', currency: 'AED', flag: '🇦🇪' },
  { code: 'India', name: 'India', currency: 'INR', flag: '🇮🇳' },
  { code: 'UK', name: 'United Kingdom', currency: 'GBP', flag: '🇬🇧' },
  { code: 'Singapore', name: 'Singapore', currency: 'SGD', flag: '🇸🇬' },
  { code: 'Ghana', name: 'Ghana', currency: 'GHS', flag: '🇬🇭' },
  { code: 'Kenya', name: 'Kenya', currency: 'KES', flag: '🇰🇪' },
  { code: 'Uganda', name: 'Uganda', currency: 'UGX', flag: '🇺🇬' },
  { code: 'Cameroon', name: 'Cameroon', currency: 'XAF', flag: '🇨🇲' },
  { code: 'Mauritius', name: 'Mauritius', currency: 'MUR', flag: '🇲🇺' },
];

const Companies = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);  // company being edited
  const [logoUploading, setLogoUploading] = useState(null);
  const [newCompany, setNewCompany] = useState({
    name: '',
    country: '',
    parent_company_id: '',
    address: '',
    currency: 'USD',
    timezone: 'UTC'
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await companiesAPI.getAll();
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name || !newCompany.country) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      await companiesAPI.create({
        ...newCompany,
        parent_company_id: newCompany.parent_company_id || null
      });
      toast.success('Company created successfully');
      setDialogOpen(false);
      setNewCompany({
        name: '',
        country: '',
        parent_company_id: '',
        address: '',
        currency: 'USD',
        timezone: 'UTC'
      });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCountryChange = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    setNewCompany({
      ...newCompany,
      country: countryCode,
      currency: country?.currency || 'USD'
    });
  };

  const openEdit = (company) => {
    setEditing({
      ...company,
      parent_company_id: company.parent_company_id || 'none',
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSubmitting(true);
    try {
      const payload = {
        name: editing.name,
        country: editing.country,
        address: editing.address || null,
        currency: editing.currency,
        timezone: editing.timezone,
        contact_email: editing.contact_email || null,
        contact_phone: editing.contact_phone || null,
        website: editing.website || null,
        registration_number: editing.registration_number || null,
        parent_company_id: editing.parent_company_id && editing.parent_company_id !== 'none' ? editing.parent_company_id : null,
      };
      await companiesAPI.update(editing.id, payload);
      toast.success('Company updated');
      setEditing(null);
      fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSubmitting(false); }
  };

  const uploadLogo = async (company, file) => {
    if (!file) return;
    setLogoUploading(company.id);
    try {
      await companiesAPI.uploadLogo(company.id, file);
      toast.success('Logo uploaded');
      fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setLogoUploading(null); }
  };

  const removeLogo = async (company) => {
    if (!window.confirm(`Remove logo for ${company.name}?`)) return;
    try {
      await companiesAPI.deleteLogo(company.id);
      toast.success('Logo removed');
      fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const getCountryFlag = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    return country?.flag || '🌍';
  };

  const getParentCompanyName = (parentId) => {
    if (!parentId) return 'Parent Company';
    const parent = companies.find(c => c.id === parentId);
    return parent?.name || 'Unknown';
  };

  const canCreate = ['super_admin', 'hr_admin'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const parentCompanies = companies.filter(c => !c.parent_company_id);
  const subsidiaries = companies.filter(c => c.parent_company_id);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Companies</h1>
          <p className="text-muted-foreground">Manage subsidiaries across 9 countries</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-company-btn">
                <Plus size={18} className="mr-2" />
                Add Company
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Company</DialogTitle>
                <DialogDescription>
                  Create a new subsidiary or branch
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    placeholder="Pride Global UAE"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    data-testid="company-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select 
                    value={newCompany.country} 
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger data-testid="company-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2">
                            <span>{country.flag}</span>
                            <span>{country.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Parent Company (Optional)</Label>
                  <Select 
                    value={newCompany.parent_company_id} 
                    onValueChange={(v) => setNewCompany({ ...newCompany, parent_company_id: v })}
                  >
                    <SelectTrigger data-testid="parent-company-select">
                      <SelectValue placeholder="Select parent company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (This is a parent company)</SelectItem>
                      {companies.filter(c => !c.parent_company_id).map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input
                      value={newCompany.currency}
                      onChange={(e) => setNewCompany({ ...newCompany, currency: e.target.value })}
                      data-testid="company-currency-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Input
                      value={newCompany.timezone}
                      onChange={(e) => setNewCompany({ ...newCompany, timezone: e.target.value })}
                      data-testid="company-timezone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    placeholder="Enter address"
                    value={newCompany.address}
                    onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                    data-testid="company-address-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateCompany} disabled={submitting} data-testid="save-company-btn">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Company
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Companies</p>
                <p className="text-2xl font-bold">{companies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Building2 className="text-emerald-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Parent Companies</p>
                <p className="text-2xl font-bold">{parentCompanies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Globe className="text-blue-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Subsidiaries</p>
                <p className="text-2xl font-bold">{subsidiaries.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map((company) => {
          const canEdit = user?.role === 'super_admin' || user?.role === 'hr_admin';
          return (
          <Card key={company.id} className="card-hover" data-testid={`company-card-${company.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-2xl overflow-hidden border">
                    {company.has_logo ? (
                      <img src={companiesAPI.logoUrl(company.id)} alt={company.name} className="w-full h-full object-contain" data-testid={`company-logo-${company.id}`} />
                    ) : (
                      <span>{getCountryFlag(company.country)}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold">{company.name}</h3>
                    <p className="text-sm text-muted-foreground">{company.country}</p>
                  </div>
                </div>
                <Badge variant={company.parent_company_id ? 'secondary' : 'default'}>
                  {company.parent_company_id ? 'Subsidiary' : 'Parent'}
                </Badge>
              </div>

              <div className="space-y-2">
                {company.parent_company_id && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Building2 size={14} className="mr-2" />
                    <span>Parent: {getParentCompanyName(company.parent_company_id)}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-muted-foreground">
                  <MapPin size={14} className="mr-2" />
                  <span>{company.address || 'No address'}</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">Currency</span>
                  <Badge variant="outline">{company.currency}</Badge>
                </div>

                {canEdit && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEdit(company)} data-testid={`edit-company-${company.id}`}>
                      <Edit size={12} className="mr-1" />Edit
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(company, e.target.files?.[0])} data-testid={`upload-logo-${company.id}`} />
                      <Button size="sm" variant="outline" asChild disabled={logoUploading === company.id}>
                        <span>
                          {logoUploading === company.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                          {company.has_logo ? 'Change Logo' : 'Logo'}
                        </span>
                      </Button>
                    </label>
                    {company.has_logo && (
                      <Button size="sm" variant="outline" onClick={() => removeLogo(company)} data-testid={`del-logo-${company.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                    <Link to={`/letterhead/${company.id}`}>
                      <Button size="sm" variant="outline" data-testid={`letterhead-${company.id}`}>
                        <FileText size={12} className="mr-1" />Letterhead
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );})}
      </div>

      {/* Edit Company Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} data-testid="edit-company-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Country</Label>
                  <Select value={editing.country} onValueChange={(v) => setEditing({...editing, country: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Currency</Label><Input value={editing.currency || ''} onChange={(e) => setEditing({...editing, currency: e.target.value})} /></div>
              </div>
              <div><Label>Address</Label><Input value={editing.address || ''} onChange={(e) => setEditing({...editing, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact Email</Label><Input type="email" value={editing.contact_email || ''} onChange={(e) => setEditing({...editing, contact_email: e.target.value})} data-testid="edit-contact-email" /></div>
                <div><Label>Contact Phone</Label><Input value={editing.contact_phone || ''} onChange={(e) => setEditing({...editing, contact_phone: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Website</Label><Input value={editing.website || ''} onChange={(e) => setEditing({...editing, website: e.target.value})} /></div>
                <div><Label>Registration #</Label><Input value={editing.registration_number || ''} onChange={(e) => setEditing({...editing, registration_number: e.target.value})} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={submitting} data-testid="save-edit-company-btn">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {companies.length === 0 && (
        <div className="text-center py-12">
          <Building2 size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No companies found. Add your first company to get started.</p>
        </div>
      )}
    </div>
  );
};

export default Companies;
